/**
 * 루미나 화면 층 신작 — 블랙 `components/sports/SportsCategoryList.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 스포츠 페이지 좌측 카테고리 .sp-cat + 종목 타일 줄 .sp-tiles).
 * WHY: SPEC §2 — 종목 목록(GET /sports/types 캐시 구독)·경기 수 집계·탭 전환 규칙은 블랙 그대로 두고,
 *      이모지 종목 아이콘은 인라인 SVG 로(서버 브라우저에 이모지 글꼴이 없어 네모로 깨진다), 국가는 시안 .cc 국가코드 칩으로 그린다.
 *   · SPORT_ICONS / DefaultSportIcon 은 SportsMatchList·경기결과·베팅내역·홈이 import 하므로 이름·형태(React 요소)를 유지한다.
 *   · COUNTRY_ISO(국가명 → ISO 코드)는 블랙 SportsMatchList 에 있던 표를 이 파일로 옮겼다 — 카테고리 목록도 국가 칩을 그려야 하는데
 *     SportsMatchList 가 이 파일을 import 하므로 반대 방향 import(순환)를 피하기 위함. 국기 URL 함수(getCountryFlagUrl)는 홈이 쓰므로 SportsMatchList 에 남긴다.
 *   · SportsSportTiles(종목 타일 줄)는 시안 §11.1 신설 요소 — 사이드바 종목 탭과 같은 상태(activeSport/filterMode)를 공유하며 경기 목록 위에 놓인다.
 *
 * 스포츠 종목/국가/리그 카테고리 목록
 * 좌측 사이드바에 표시되며, 탭 전환으로 종목/국가/리그별 브라우징 지원
 *
 * 종목 목록은 API(/sports/types)에서 동적으로 가져옴 (하드코딩 없음)
 * 경기 수는 부모에서 전달받은 matches(전체)에서 sportType별로 집계
 */
import React, { useState, useEffect, useMemo } from 'react';
import sportsService from '../../services/sports';

/* ── 종목 아이콘: 인라인 SVG(선 아이콘, currentColor) — 크기·색은 CSS(.sp-cat-list .ic svg / .sp-tile .ic svg / .flag svg)가 정한다 ── */
// width/height 1em: 글꼴 크기를 따라가던 이모지 자리를 그대로 채운다(홈·베팅내역 등 다른 화면이 크기 CSS 없이 import 해도 300×150 기본 크기로 터지지 않게)
const SvgIcon = ({ label, children }) => (
  <svg className="sport-svg-icon" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label={label}>
    {children}
  </svg>
);

export const SPORT_ICONS = {
  all: (
    <SvgIcon label="전체"><path d="M4 7h16M4 12h16M4 17h16" /></SvgIcon>
  ),
  soccer: (
    <SvgIcon label="축구"><circle cx="12" cy="12" r="9" /><path d="M12 8l3.8 2.8-1.5 4.4h-4.6L8.2 10.8z" /><path d="M12 3v5M4.2 9.5l4 1.3M19.8 9.5l-4 1.3M7 20l2.7-4.8M17 20l-2.7-4.8" /></SvgIcon>
  ),
  basket: (
    <SvgIcon label="농구"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c-3 3-3 15 0 18M12 3c3 3 3 15 0 18" /></SvgIcon>
  ),
  baseball: (
    <SvgIcon label="야구"><circle cx="12" cy="12" r="9" /><path d="M6 5c3 3 3 11 0 14M18 5c-3 3-3 11 0 14" /></SvgIcon>
  ),
  volleyball: (
    <SvgIcon label="배구"><circle cx="12" cy="12" r="9" /><path d="M12 3c2 6 6 9 9 9M3 12c6 0 9 4 9 9M5 6c4 2 10 2 14 0" /></SvgIcon>
  ),
  tennis: (
    <SvgIcon label="테니스"><circle cx="12" cy="12" r="9" /><path d="M5.5 5.5c4 2 9 7 11 13M18.5 5.5c-4 2-9 7-11 13" /></SvgIcon>
  ),
  hockey: (
    <SvgIcon label="하키"><path d="M4 4l8 12h6M9 20h9M18 16v4" /><circle cx="6" cy="19" r="1.5" /></SvgIcon>
  ),
  esports: (
    <SvgIcon label="e스포츠"><rect x="3" y="8" width="18" height="10" rx="5" /><path d="M8 11v4M6 13h4" /><circle cx="15.5" cy="12" r=".9" /><circle cx="18" cy="14" r=".9" /></SvgIcon>
  ),
  amfootball: (
    <SvgIcon label="미식축구"><ellipse cx="12" cy="12" rx="10" ry="6" transform="rotate(-45 12 12)" /><path d="M9 15l6-6M10 12l2 2M12 10l2 2" /></SvgIcon>
  ),
  cricket: (
    <SvgIcon label="크리켓"><path d="M4 20l6-6M10 14l7-7 3 3-7 7z" /><circle cx="6" cy="6" r="2" /></SvgIcon>
  ),
  handball: (
    <SvgIcon label="핸드볼"><circle cx="16" cy="6" r="3" /><path d="M4 21c0-4 2-7 5-8l3-1M9 13l2-6 2 1-1 5" /></SvgIcon>
  ),
};

/** 기본 종목 아이콘 (매핑에 없는 종목용) */
export const DefaultSportIcon = () => (
  <SvgIcon label="기타"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /></SvgIcon>
);

/** 국가 영문명 → ISO 3166-1 alpha-2 코드 매핑 (블랙 SportsMatchList 에서 이동 — 국기 URL·국가코드 칩 공용) */
export const COUNTRY_ISO = {
  'Algeria':'dz','Argentina':'ar','Armenia':'am','Australia':'au','Austria':'at',
  'Azerbaijan':'az','Bahrain':'bh','Bangladesh':'bd','Belarus':'by','Belgium':'be',
  'Bolivia':'bo','Bosnia and Herzegovina':'ba','Brazil':'br','Bulgaria':'bg',
  'Cambodia':'kh','Cameroon':'cm','Canada':'ca','Chile':'cl','China':'cn',
  'Colombia':'co','Costa Rica':'cr','Croatia':'hr','Cyprus':'cy','Czech Republic':'cz',
  'Denmark':'dk','Ecuador':'ec','Egypt':'eg','England':'gb-eng','Estonia':'ee',
  'Ethiopia':'et','Finland':'fi','France':'fr','Georgia':'ge','Germany':'de',
  'Ghana':'gh','Greece':'gr','Guatemala':'gt','Honduras':'hn','Hong Kong':'hk',
  'Hungary':'hu','Iceland':'is','India':'in','Indonesia':'id','Iran':'ir','Iraq':'iq',
  'Ireland':'ie','Israel':'il','Italy':'it','Jamaica':'jm','Japan':'jp','Jordan':'jo',
  'Kazakhstan':'kz','Kenya':'ke','Korea Republic':'kr','South Korea':'kr','Kosovo':'xk',
  'Kuwait':'kw','Latvia':'lv','Lebanon':'lb','Lithuania':'lt','Luxembourg':'lu',
  'Malaysia':'my','Malta':'mt','Mexico':'mx','Moldova':'md','Montenegro':'me',
  'Morocco':'ma','Netherlands':'nl','New Zealand':'nz','Nigeria':'ng','North Macedonia':'mk',
  'Northern Ireland':'gb-nir','Norway':'no','Oman':'om','Panama':'pa','Paraguay':'py',
  'Peru':'pe','Philippines':'ph','Poland':'pl','Portugal':'pt','Qatar':'qa',
  'Romania':'ro','Russia':'ru','Saudi Arabia':'sa','Scotland':'gb-sct','Senegal':'sn',
  'Serbia':'rs','Singapore':'sg','Slovakia':'sk','Slovenia':'si','South Africa':'za',
  'Spain':'es','Sweden':'se','Switzerland':'ch','Taiwan':'tw','Thailand':'th',
  'Tunisia':'tn','Turkey':'tr','UAE':'ae','Ukraine':'ua','United States':'us','USA':'us',
  'Uruguay':'uy','Uzbekistan':'uz','Venezuela':'ve','Vietnam':'vn','Wales':'gb-wls',
  'International':'un','World':'un',
  // 한글 국가명
  '대한민국':'kr','한국':'kr','일본':'jp','중국':'cn','호주':'au','브라질':'br',
  '아르헨티나':'ar','독일':'de','프랑스':'fr','영국':'gb','이탈리아':'it','스페인':'es',
  '네덜란드':'nl','포르투갈':'pt','벨기에':'be','스위스':'ch','오스트리아':'at',
  '덴마크':'dk','노르웨이':'no','스웨덴':'se','핀란드':'fi','폴란드':'pl',
  '체코':'cz','러시아':'ru','우크라이나':'ua','터키':'tr','그리스':'gr',
  '크로아티아':'hr','세르비아':'rs','루마니아':'ro','불가리아':'bg','헝가리':'hu',
  '슬로바키아':'sk','아이슬란드':'is','아일랜드':'ie','이스라엘':'il','이란':'ir',
  '사우디아라비아':'sa','카타르':'qa','멕시코':'mx','미국':'us','캐나다':'ca',
  '칠레':'cl','콜롬비아':'co','페루':'pe','에콰도르':'ec','우루과이':'uy',
  '파라과이':'py','볼리비아':'bo','베네수엘라':'ve','이집트':'eg','남아프리카':'za',
  '모로코':'ma','나이지리아':'ng','케냐':'ke','가나':'gh','카메룬':'cm',
  '튀니지':'tn','알제리':'dz','인도':'in','태국':'th','베트남':'vn',
  '인도네시아':'id','말레이시아':'my','필리핀':'ph','싱가포르':'sg','대만':'tw',
  '홍콩':'hk','뉴질랜드':'nz','국제':'un',
  '보스니아 헤르체고비나':'ba','벨라루스':'by','코스타리카':'cr','온두라스':'hn',
};

/**
 * 국가 칩 글자(시안 .cc: KR·GB·INT …). 유저 API 가 주는 league.countryCode 를 1순위, 없으면 국가명 표로 찾는다.
 * 'gb-eng' 같은 하위 코드는 뒷부분(ENG), 국제(un)는 시안 표기 INT. 둘 다 없으면 null(호출측이 지구본 아이콘으로 대체).
 */
export const countryCodeOf = (name, code) => {
  const raw = String(code || COUNTRY_ISO[name] || '').toLowerCase();
  if (!raw) return null;
  if (raw === 'un') return 'INT';
  const part = raw.includes('-') ? raw.split('-')[1] : raw;
  return part.toUpperCase().slice(0, 3);
};

/** 국가 코드 칩 — 코드가 없는 국가는 지구본 아이콘 칩(블랙 GlobeIcon 승계) */
export const CountryChip = ({ name, code }) => {
  const cc = countryCodeOf(name, code);
  if (cc) return <span className="cc" title={name || ''}>{cc}</span>;
  return (
    <span className="cc cc-glob" title={name || ''} aria-hidden="true">
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
      </svg>
    </span>
  );
};

/** 리그 탭용 쉴드(방패) 아이콘 */
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2L4 6v5c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V6l-8-4z" />
    <path d="M12 8v4M12 15h.01" />
  </svg>
);

/** 사이드바 탭 정의 */
const SIDEBAR_TABS = [
  { id: 'sport', label: '종목' },
  { id: 'country', label: '국가' },
  { id: 'league', label: '리그' },
];

const SportsCategoryList = ({
  activeSport,
  onSportChange,
  activeTab,
  matches,
  activeCountry,
  onCountryChange,
  activeLeague,
  onLeagueChange,
}) => {
  const [sidebarTab, setSidebarTab] = useState('sport');
  // API에서 동적으로 가져온 종목 목록
  const [sportTypes, setSportTypes] = useState([]);

  // 종목 목록 로드 (API에서 동적)
  useEffect(() => {
    let cancelled = false;

    sportsService.fetchSportTypes().then(types => {
      if (!cancelled && types.length > 0) {
        setSportTypes(types);
      }
    });

    // 이후 캐시 업데이트 감지
    const unsubscribe = sportsService.onSportTypesChange(types => {
      if (!cancelled && types) {
        setSportTypes(types);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // [2026-05-06 스페셜 카운트 정합]
  //   사용자 사양: 스페셜 탭 사이드바 카운트는 실제 노출되는(special 마켓 보유) 매치 수와 일치.
  //   admin-api /fixtures/table 후처리가 fixture 별 hasSpecialMarket boolean 을 부착하고
  //   user-api adaptFixtureTable 이 match.hasSpecialMarket 으로 전달한다.
  //   isSpecialTab 일 때 hasSpecialMarket=true 매치만 카운트·노출 대상으로 사용.
  const isSpecialTabActive = activeTab === 'prematch_special';
  const filteredMatches = useMemo(() => {
    if (!isSpecialTabActive) return matches;
    if (!matches || matches.length === 0) return matches;
    return matches.filter(m => m && m.hasSpecialMarket === true);
  }, [matches, isSpecialTabActive]);

  /** matches에서 종목별 경기(fixture) 수 집계 */
  const sportCounts = useMemo(() => {
    const counts = {};
    let total = 0;
    if (filteredMatches && filteredMatches.length > 0) {
      for (const match of filteredMatches) {
        const sk = match.sportType;
        if (sk) {
          counts[sk] = (counts[sk] || 0) + 1;
          total += 1;
        }
      }
    }
    counts['all'] = total;
    return counts;
  }, [filteredMatches]);

  /** 국가 목록: matches에서 고유 국가를 추출하고 경기 수를 계산 */
  const countryList = useMemo(() => {
    if (!filteredMatches || filteredMatches.length === 0) return [];
    const countryMap = {};
    filteredMatches.forEach(match => {
      const country = match.league?.country;
      if (country) {
        countryMap[country] = (countryMap[country] || 0) + 1;
      }
    });
    const list = Object.entries(countryMap).map(([name, count]) => ({ name, count }));
    list.sort((a, b) => b.count - a.count);
    return list;
  }, [filteredMatches]);

  /** 국가명 → 유저 API 국가 코드(league.countryCode) — 국가 칩 글자용(표시 전용) */
  const countryCodeByName = useMemo(() => {
    const map = {};
    (filteredMatches || []).forEach(match => {
      const country = match?.league?.country;
      const code = match?.league?.countryCode;
      if (country && code && !map[country]) map[country] = code;
    });
    return map;
  }, [filteredMatches]);

  /** 리그 목록: matches에서 고유 리그를 추출하고 경기 수를 계산 */
  const leagueList = useMemo(() => {
    if (!filteredMatches || filteredMatches.length === 0) return [];
    const leagueMap = {};
    filteredMatches.forEach(match => {
      const league = match.league;
      if (league?.leagueId && league?.name) {
        if (!leagueMap[league.leagueId]) {
          leagueMap[league.leagueId] = { leagueId: league.leagueId, name: league.name, count: 0 };
        }
        leagueMap[league.leagueId].count += 1;
      }
    });
    const list = Object.values(leagueMap);
    list.sort((a, b) => b.count - a.count);
    return list;
  }, [filteredMatches]);

  /** 탭 전환 시 필터 초기화 */
  const handleTabSwitch = (tabId) => {
    setSidebarTab(tabId);
    if (tabId === 'sport') {
      onSportChange('all');
    } else if (tabId === 'country') {
      if (onCountryChange) onCountryChange('all');
    } else if (tabId === 'league') {
      if (onLeagueChange) onLeagueChange('all');
    }
  };

  const totalMatchCount = matches ? matches.length : 0;

  return (
    <div className="sp-cat">
      {/* 탭 헤더: 종목 / 국가 / 리그 — 시안 세그먼트(.sp-cat-tabs, 현재 = 차콜) */}
      <div className="sp-cat-tabs" role="tablist">
        {SIDEBAR_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`sp-cat-tab ${sidebarTab === tab.id ? 'on' : ''}`}
            onClick={() => handleTabSwitch(tab.id)}
            role="tab"
            aria-selected={sidebarTab === tab.id}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 종목 탭 — API에서 동적으로 가져온 종목 표시 */}
      {sidebarTab === 'sport' && (
        <ul className="sp-cat-list">
          {/* 전체 항목 */}
          <li>
            <button
              type="button"
              className={`sp-cat-item ${activeSport === 'all' ? 'on' : ''}`}
              onClick={() => onSportChange('all')}
            >
              <span className="ic">{SPORT_ICONS.all}</span>
              <span className="nm">전체</span>
              {totalMatchCount > 0 && (
                <span className="n">{totalMatchCount}</span>
              )}
            </button>
          </li>
          {/* API에서 가져온 종목 목록 (sortOrder 순) */}
          {sportTypes
            .filter(sport => sport.isActive !== false)
            .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
            .map(sport => {
              const count = sportCounts[sport.id] || 0;
              return (
                <li key={sport.id}>
                  <button
                    type="button"
                    className={`sp-cat-item ${activeSport === sport.id ? 'on' : ''}`}
                    onClick={() => onSportChange(sport.id)}
                  >
                    <span className="ic">
                      {SPORT_ICONS[sport.id] || <DefaultSportIcon />}
                    </span>
                    <span className="nm">{sport.name}</span>
                    {count > 0 && (
                      <span className="n">{count}</span>
                    )}
                  </button>
                </li>
              );
            })}
        </ul>
      )}

      {/* 국가 탭 — 국가 코드 칩(.cc) + 국가명 + 경기 수 */}
      {sidebarTab === 'country' && (
        <ul className="sp-cat-list">
          <li>
            <button
              type="button"
              className={`sp-cat-item ${activeCountry === 'all' ? 'on' : ''}`}
              onClick={() => onCountryChange && onCountryChange('all')}
            >
              <span className="ic">{SPORT_ICONS.all}</span>
              <span className="nm">전체</span>
              {totalMatchCount > 0 && (
                <span className="n">{totalMatchCount}</span>
              )}
            </button>
          </li>
          {countryList.map(country => (
            <li key={country.name}>
              <button
                type="button"
                className={`sp-cat-item ${activeCountry === country.name ? 'on' : ''}`}
                onClick={() => onCountryChange && onCountryChange(country.name)}
              >
                <span className="ic ic-cc"><CountryChip name={country.name} code={countryCodeByName[country.name]} /></span>
                <span className="nm">{country.name}</span>
                {country.count > 0 && (
                  <span className="n">{country.count}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 리그 탭 */}
      {sidebarTab === 'league' && (
        <ul className="sp-cat-list">
          <li>
            <button
              type="button"
              className={`sp-cat-item ${activeLeague === 'all' ? 'on' : ''}`}
              onClick={() => onLeagueChange && onLeagueChange('all')}
            >
              <span className="ic">{SPORT_ICONS.all}</span>
              <span className="nm">전체</span>
              {totalMatchCount > 0 && (
                <span className="n">{totalMatchCount}</span>
              )}
            </button>
          </li>
          {leagueList.map(league => (
            <li key={league.leagueId}>
              <button
                type="button"
                className={`sp-cat-item ${activeLeague === league.leagueId ? 'on' : ''}`}
                onClick={() => onLeagueChange && onLeagueChange(league.leagueId)}
              >
                <span className="ic"><ShieldIcon /></span>
                <span className="nm">{league.name}</span>
                {league.count > 0 && (
                  <span className="n">{league.count}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * 종목 타일 줄(시안 §11.1 신설 — 전체 + 종목별 건수). 경기 목록 위에 놓이며 사이드바 종목 탭과 같은 상태를 공유한다:
 *   활성 = filterMode==='sport' && activeSport===id, 클릭 = SportsPage.handleSportChange(=사이드바 종목 클릭과 동일 핸들러).
 * 종목 목록·건수 규칙은 위 SportsCategoryList 와 같다(/sports/types 캐시 구독, 스페셜 탭은 hasSpecialMarket 매치만 집계).
 * 모바일(≤1100)에서는 사이드바가 접혀 있어 이 줄이 주 전환 수단이 된다(조사 근거 SPEC §11.1).
 */
export const SportsSportTiles = ({ activeSport, filterMode, onSportChange, activeTab, matches }) => {
  const [sportTypes, setSportTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    sportsService.fetchSportTypes().then(types => {
      if (!cancelled && types && types.length > 0) setSportTypes(types);
    });
    const unsubscribe = sportsService.onSportTypesChange(types => {
      if (!cancelled && types) setSportTypes(types);
    });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  const isSpecialTabActive = activeTab === 'prematch_special';
  const filteredMatches = useMemo(() => {
    if (!isSpecialTabActive) return matches;
    if (!matches || matches.length === 0) return matches;
    return matches.filter(m => m && m.hasSpecialMarket === true);
  }, [matches, isSpecialTabActive]);

  const sportCounts = useMemo(() => {
    const counts = {};
    (filteredMatches || []).forEach(match => {
      const sk = match?.sportType;
      if (sk) counts[sk] = (counts[sk] || 0) + 1;
    });
    return counts;
  }, [filteredMatches]);

  const totalMatchCount = matches ? matches.length : 0;
  const isSportMode = filterMode === 'sport';

  return (
    <div className="sp-tiles" role="tablist" aria-label="종목 바로 가기">
      <button
        type="button"
        role="tab"
        aria-selected={isSportMode && activeSport === 'all'}
        className={`sp-tile ${isSportMode && activeSport === 'all' ? 'on' : ''}`}
        onClick={() => onSportChange('all')}
      >
        <span className="ic">{SPORT_ICONS.all}</span>
        <span>전체</span>
        <b>{totalMatchCount}</b>
      </button>
      {sportTypes
        .filter(sport => sport.isActive !== false)
        .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
        .map(sport => (
          <button
            key={sport.id}
            type="button"
            role="tab"
            aria-selected={isSportMode && activeSport === sport.id}
            className={`sp-tile ${isSportMode && activeSport === sport.id ? 'on' : ''}`}
            onClick={() => onSportChange(sport.id)}
          >
            <span className="ic">{SPORT_ICONS[sport.id] || <DefaultSportIcon />}</span>
            <span>{sport.name}</span>
            <b>{sportCounts[sport.id] || 0}</b>
          </button>
        ))}
    </div>
  );
};

export default SportsCategoryList;
